import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiPhone, FiChevronDown } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import Logo from './Logo';
import {
  handleHashNavigation,
  navigateToConsultation
} from '../utils/navigation';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showSubMenu, setShowSubMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle hash navigation on page load
  useEffect(() => {
    handleHashNavigation();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollTop / docHeight;
      
      setScrolled(scrollTop > 50);
      setScrollProgress(scrollPercent * 100);

      // Active section detection for home page
      if (location.pathname === '/') {
        const sections = ['hero', 'services', 'transformations', 'recipes'];
        let current = '';
        
        sections.forEach(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 100) {
              current = section;
            }
          }
        });
        
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    setIsOpen(false);
    setShowSubMenu(false);
    // Reset active section when navigating away from home
    if (location.pathname !== '/') {
      setActiveSection('');
    }
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home', id: 'home' },
    { 
      to: '/services', 
      label: 'Services', 
      id: 'services',
      subItems: [
        { to: '/services#weight-loss', label: 'Weight Loss Programs' },
        { to: '/services#weight-gain', label: 'Weight Gain Programs' },
        { to: '/disease-management', label: 'Disease Management' },
        { to: '/services#skin-toning', label: 'Skin Toning' },
        { to: '/services#virtual-coaching', label: 'Virtual Coaching' },
        { to: '/#bmi-calculator', label: 'BMI Calculator', special: 'bmi' }
      ]
    },
    { to: '/transformations', label: 'Transformations', id: 'transformations' },
    { to: '/recipes', label: 'Recipes', id: 'recipes' },
    { to: '/blog', label: 'Blog', id: 'blog' },
    { to: '/about', label: 'About', id: 'about' },
    { to: '/contact', label: 'Contact', id: 'contact' },
  ];

  const isActive = (path, id) => {
    if (location.pathname === '/' && activeSection === id) return true;
    return location.pathname === path;
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-primary-200 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-secondary-500 to-accent-500"
          style={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      <nav className={`sticky top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'nav-scrolled' 
          : 'bg-gradient-to-r from-primary-50/90 via-white/95 to-primary-50/90'
      }`}>
        <div className="container-max">
          <div className="flex justify-between items-center py-3">
                         {/* Logo */}
             <Link to="/" className="group">
               <Logo 
                 variant="main" 
                 size="navbar" 
                 animated={true}
                 className="group-hover:scale-105 transition-transform duration-300"
               />
             </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.to} className="relative group">
                  {link.subItems ? (
                    // Dropdown Menu
                    <div
                      onMouseEnter={() => setShowSubMenu(link.to)}
                      onMouseLeave={() => setShowSubMenu(false)}
                      className="relative"
                    >
                      <Link
                        to={link.to}
                        className={`nav-link font-accent flex items-center space-x-1 ${
                          isActive(link.to, link.id)
                            ? 'active text-secondary-600'
                            : scrolled
                            ? 'text-neutral-700 hover:text-secondary-600'
                            : 'text-neutral-800 hover:text-secondary-600'
                        }`}
                      >
                        <span>{link.label}</span>
                        <motion.div
                          animate={{ rotate: showSubMenu === link.to ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <FiChevronDown className="w-4 h-4" />
                        </motion.div>
                      </Link>
                      
                      <AnimatePresence>
                        {showSubMenu === link.to && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-brand-lg border border-primary-200 py-2 z-60"
                          >
                            {link.subItems.map((subItem, index) => (
                              <Link
                                key={subItem.to}
                                to={subItem.to}
                                className={`block px-4 py-3 text-sm transition-colors font-accent ${
                                  subItem.special === 'bmi'
                                    ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold'
                                    : 'text-neutral-700 hover:text-secondary-600 hover:bg-primary-50'
                                }`}
                              >
                                {subItem.special === 'bmi' && <FaCalculator className="w-4 h-4 inline mr-2" />}
                                {subItem.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    // Regular Link
                    <Link
                      to={link.to}
                      onClick={() => {
                        setIsOpen(false);
                        // Let React Router handle navigation to top of page
                      }}
                      className={`nav-link font-accent ${
                        isActive(link.to, link.id)
                          ? 'active text-secondary-600'
                          : scrolled
                          ? 'text-neutral-700 hover:text-secondary-600'
                          : 'text-neutral-800 hover:text-secondary-600'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact#consultation"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateToConsultation(navigate);
                  }}
                  className="btn-primary flex items-center space-x-2 font-accent"
                >
                  <FiPhone className="w-4 h-4" />
                  <span>Enroll Now</span>
                </Link>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                scrolled
                  ? 'text-neutral-700 hover:bg-primary-100'
                  : 'text-neutral-800 hover:bg-white/20'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white/95 backdrop-blur-md border-t border-primary-200 shadow-lg"
            >
              <div className="container-max py-6">
                <div className="flex flex-col space-y-2">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.to}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={link.to}
                        onClick={() => {
                          setIsOpen(false);
                          // Let React Router handle navigation to top of page
                        }}
                        className={`font-medium py-3 px-4 rounded-lg transition-all font-accent block ${
                          isActive(link.to, link.id)
                            ? 'text-secondary-600 bg-secondary-50 border-l-4 border-secondary-500'
                            : 'text-neutral-700 hover:text-secondary-600 hover:bg-primary-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                      
                      {/* Mobile Submenu */}
                      {link.subItems && (
                        <div className="ml-4 mt-2 space-y-1">
                          {link.subItems.map((subItem) => (
                            <Link
                              key={subItem.to}
                              to={subItem.to}
                              className={`block py-2 px-4 text-sm rounded-md transition-colors ${
                                subItem.special === 'bmi'
                                  ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold'
                                  : 'text-neutral-600 hover:text-secondary-600 hover:bg-primary-50'
                              }`}
                            >
                                                             {subItem.special === 'bmi' && <FaCalculator className="w-4 h-4 inline mr-2" />}
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: navLinks.length * 0.1 }}
                    className="pt-4"
                  >
                    <Link
                      to="/contact#consultation"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsOpen(false);
                        navigateToConsultation(navigate);
                      }}
                      className="btn-primary flex items-center justify-center space-x-2 font-accent"
                    >
                      <FiPhone className="w-4 h-4" />
                      <span>Enroll Now</span>
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;